import React from 'react';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { getDeviationPrice } from '../../../../utils/deviations';
import DetailsRightSide from '../../../layouts/DetailsRightSide';
import s from './AdoptableDetailsRightSide.scss';

interface Props {
  deviation: PapiDeviation;
  hideSold?: boolean;
  hidePrice?: boolean;
}

const AdoptableDetailsRightSide: React.FC<Props> = ({ deviation, hideSold, hidePrice }) => {
  const { t } = useTranslation();
  const { dollarsPrice: fixedDollarsPrice, pointsPrice: fixedPointsPrice } =
    getDeviationPrice(deviation);
  const offerPrice = deviation.adoptable?.offers?.offerPrice;

  if (!deviation.adoptable) {
    return null;
  }

  const { thumbSaleState, thumbSaleType } = deviation.adoptable;

  const isFixedPriceOnly = thumbSaleType === 'fixed';
  const isOffersOnly = thumbSaleType === 'offer';
  const isOffersAndFixedPrice = thumbSaleType === 'both';

  const renderOfferPriceInDollars = (prefix = '') => (hidePrice ? null : `${prefix}$${offerPrice}`);
  const renderFixedPriceInDollars = (prefix = '') =>
    hidePrice ? null : `${prefix}$${parseInt(fixedDollarsPrice)}`;
  const renderFixedPriceInPoints = () =>
    hidePrice ? null : (
      <>
        <PointsIcon size={IconSize.TINY} disableDefaultColors />
        {fixedPointsPrice}
      </>
    );

  const renderSold = () =>
    hideSold ? null : <div className={s['warning']}>{t('shop.thumb.adoptable.status.sold')}</div>;

  // default values we fallback to
  let firstRow: React.ReactNode = renderFixedPriceInDollars();
  let secondRow: React.ReactNode = renderFixedPriceInPoints();

  if (isFixedPriceOnly) {
    // FIXED PRICE
    if (thumbSaleState === 'unlisted') {
      // Unlisted
      secondRow = <div className={s['warning']}>{t('shop.thumb.adoptable.status.saleEnded')}</div>;
    } else if (thumbSaleState === 'sold') {
      // Sold
      secondRow = renderSold();
    }
  } else if (isOffersOnly) {
    // OFFERS
    if (thumbSaleState === 'open') {
      // Open
      firstRow = renderOfferPriceInDollars();
      secondRow = t('shop.thumb.adoptable.CTA.startingOffer');
    } else if (thumbSaleState === 'pending') {
      // Pending
      firstRow = t('shop.thumb.adoptable.CTA.offersLocked');
      secondRow = t('shop.thumb.adoptable.status.pending');
    } else if (thumbSaleState === 'expired') {
      // Expired
      firstRow = <>&nbsp;</>;
      secondRow = <div className={s['warning']}>{t('shop.thumb.adoptable.status.expired')}</div>;
    } else if (thumbSaleState === 'unlisted') {
      // Unlisted
      firstRow = <>&nbsp;</>;
      secondRow = <div className={s['warning']}>{t('shop.thumb.adoptable.status.saleEnded')}</div>;
    } else if (thumbSaleState === 'sold') {
      // Sold
      firstRow = renderOfferPriceInDollars();
      secondRow = renderSold();
    }
  } else if (isOffersAndFixedPrice) {
    // OFFERS & FIXED PRICE
    if (thumbSaleState === 'open') {
      // Open
      firstRow = t('shop.thumb.adoptable.CTA.bestOffer');
      secondRow = renderFixedPriceInDollars(/* prefix */ `${t('common.or')} `);
    } else if (thumbSaleState === 'pending') {
      // Pending
      firstRow = renderFixedPriceInDollars();
      secondRow = t('shop.thumb.adoptable.CTA.buyItNow');
    } else if (thumbSaleState === 'expired') {
      // Expired
      firstRow = renderFixedPriceInDollars();
      secondRow = t('shop.thumb.adoptable.CTA.buyItNow');
    } else if (thumbSaleState === 'unlisted') {
      // Unlisted
      firstRow = <>&nbsp;</>;
      secondRow = <div className={s['warning']}>{t('shop.thumb.adoptable.status.saleEnded')}</div>;
    } else if (thumbSaleState === 'sold') {
      // Sold
      firstRow = renderFixedPriceInDollars();
      secondRow = renderSold();
    }
  }

  return (
    <DetailsRightSide firstRow={firstRow} secondRow={secondRow} secondRowClassName={s['row-gap']} />
  );
};
AdoptableDetailsRightSide.displayName = 'AdoptableDetailsRightSide';

export default AdoptableDetailsRightSide;
